import { useDurableFilterAtomMirror } from '@/state/durableFilters/useDurableFilterAtomMirror';
import * as Sentry from '@sentry/react';
import { TagFilter } from 'gql-types';
import { atom, useAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { useMemo } from 'react';
import { z } from 'zod';

import { atomWithSessionStorage } from '../utils/browserStorage';

const selectedTagIdsByOrgSchema = z.record(z.string(), z.array(z.string()));

type SelectedTagIdsByOrg = z.infer<typeof selectedTagIdsByOrgSchema>;

const DASHBOARD_TAGS_STORAGE_KEY = 'selectedTagsByOrg';
const PATIENT_TAGS_STORAGE_KEY = 'selectedPatientTagsByOrg';

// One shared empty selection, so the mirror compares against a stable value.
const NO_SELECTED_TAG_IDS: SelectedTagIdsByOrg = {};

// Builds the atoms backing one surface's tag selection. Each surface stores its
// selection under its own key, so the selections stay independent.
const createSelectedTagIdAtoms = (storageKey: string) => {
    // Stores selected tag IDs keyed by org ID so each org remembers its own selection.
    const byOrgAtom = atomWithSessionStorage(
        storageKey,
        NO_SELECTED_TAG_IDS,
        selectedTagIdsByOrgSchema,
    );

    // Derives a per-org atom for reading/writing that org's selected tag IDs.
    const forOrg = atomFamily((orgId: string) =>
        atom(
            (get) => {
                const allTagIds = get(byOrgAtom);
                return orgId in allTagIds ? allTagIds[orgId] : [];
            },
            (get, set, value: string[]) => {
                const prev = get(byOrgAtom);
                set(byOrgAtom, { ...prev, [orgId]: value });
            },
        ),
    );

    return { byOrgAtom, forOrg, storageKey };
};

type SelectedTagIdAtoms = ReturnType<typeof createSelectedTagIdAtoms>;

const dashboardTagAtoms = createSelectedTagIdAtoms(DASHBOARD_TAGS_STORAGE_KEY);
const patientTagAtoms = createSelectedTagIdAtoms(PATIENT_TAGS_STORAGE_KEY);

/**
 * Builds a TagFilter that ORs every selected tag, so a patient matches when it
 * carries at least one of them, whichever group each tag belongs to.
 */
export function buildTagFilter(selectedTagIds: string[]): TagFilter | null {
    if (selectedTagIds.length === 0) {
        return null;
    }

    // OR across all selected tags — patient must have at least one of the selected tags.
    return { id: { inList: selectedTagIds } };
}

/**
 * Drops selected IDs that no tag carries any more. Returns the original array
 * when every ID is still current, so a caller can skip writing by comparing
 * identity.
 */
export function pruneMissingTagIds(
    selectedTagIds: string[],
    availableTagIds: Iterable<string>,
): string[] {
    if (selectedTagIds.length === 0) {
        return selectedTagIds;
    }

    const available = new Set(availableTagIds);
    const current = selectedTagIds.filter((id) => available.has(id));

    return current.length === selectedTagIds.length ? selectedTagIds : current;
}

// A selection stored before the shape dropped to IDs holds a copy of each tag.
const legacySelectedTagsByOrgSchema = z.record(z.string(), z.array(z.object({ id: z.string() })));

/**
 * Rewrites a stored selection that still holds tag copies into the IDs the
 * current shape stores, so a selection made by an earlier build survives. A
 * value already in the current shape, or in neither, is left alone.
 */
export const migrateStoredTagSelectionsToIds = () => {
    [DASHBOARD_TAGS_STORAGE_KEY, PATIENT_TAGS_STORAGE_KEY].forEach((key) => {
        try {
            const stored = window.sessionStorage.getItem(key);
            if (stored === null) {
                return;
            }

            const parsed: unknown = JSON.parse(stored);
            if (selectedTagIdsByOrgSchema.safeParse(parsed).success) {
                return;
            }

            const legacy = legacySelectedTagsByOrgSchema.safeParse(parsed);
            if (!legacy.success) {
                return;
            }

            const byOrg = Object.fromEntries(
                Object.entries(legacy.data).map(([orgId, tags]) => [
                    orgId,
                    tags.map((tag) => tag.id),
                ]),
            );
            window.sessionStorage.setItem(key, JSON.stringify(byOrg));
        } catch (error) {
            Sentry.captureException(error);
        }
    });
};

const useTagFilter = ({ forOrg }: SelectedTagIdAtoms, orgId: string) => {
    const orgAtom = useMemo(() => forOrg(orgId), [forOrg, orgId]);
    const [selectedTagIds, setSelectedTagIds] = useAtom(orgAtom);

    const tagFilter: TagFilter | null = useMemo(
        () => buildTagFilter(selectedTagIds),
        [selectedTagIds],
    );

    return { selectedTagIds, setSelectedTagIds, tagFilter };
};

const useTagFiltersDurableMirror = (
    { byOrgAtom, storageKey }: SelectedTagIdAtoms,
    enabled: boolean,
) =>
    useDurableFilterAtomMirror({
        filtersAtom: byOrgAtom,
        storageKey,
        storage: window.sessionStorage,
        emptyValue: NO_SELECTED_TAG_IDS,
        schema: selectedTagIdsByOrgSchema,
        enabled,
    });

export const useDashboardTagFilter = (orgId: string) => useTagFilter(dashboardTagAtoms, orgId);

export const useDashboardTagFiltersDurableMirror = (enabled = true) =>
    useTagFiltersDurableMirror(dashboardTagAtoms, enabled);

export const usePatientTagFilter = (orgId: string) => useTagFilter(patientTagAtoms, orgId);

export const usePatientTagFiltersDurableMirror = (enabled = true) =>
    useTagFiltersDurableMirror(patientTagAtoms, enabled);
